import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { buildTheme, type ThemeMode } from '@/styles/theme';

export type ThemeChoice = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ig_theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeModeValue {
  choice: ThemeChoice;
  mode: ThemeMode;
  setChoice: (choice: ThemeChoice) => void;
}

function readChoice(): ThemeChoice {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
  } catch {
    // Storage can be blocked; fall back to the device setting.
  }
  return 'system';
}

function deviceIsDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

const ThemeModeContext = createContext<ThemeModeValue | null>(null);

// ThemeModeProvider picks the Night gallery mode (system, light or dark) and themes the app.
export function ThemeModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);
  const [deviceDark, setDeviceDark] = useState<boolean>(deviceIsDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setDeviceDark(event.matches);
    setDeviceDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode: ThemeMode = choice === 'system' ? (deviceDark ? 'dark' : 'light') : choice;

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const setChoice = useCallback((next: ThemeChoice) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The choice still applies for this visit.
    }
    setChoiceState(next);
  }, []);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(() => ({ choice, mode, setChoice }), [choice, mode, setChoice]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

// useThemeMode returns the stored choice, the resolved mode and the setter.
export function useThemeMode(): ThemeModeValue {
  const value = useContext(ThemeModeContext);
  if (!value) {
    throw new Error('useThemeMode must be used inside ThemeModeProvider');
  }
  return value;
}
